import { FC, useCallback, useLayoutEffect, useState } from "react";
import PageTitle from "../../../../assets/components/PageTitle/PageTitle";
import LicenseKeysNotActivatedSearchBar from "./LicenseKeysNotActivatedTable/LicenseKeysNotActivatedSearchBar";
import { Products } from "../../../../models/models";
import { SearchBarDataHandler } from "../../../../assets/components/SearchBar/SearchBar";
import { toast } from "react-toastify";
import {
    useDownloadCSVNotActivatedLicensesMutation,
    useGetProductsListQuery,
    useGetReportNotActivatedLicensesListQuery,
} from "../../../../redux/RTKQuery/api";
import { Loading, uiSlicedActions } from "../../../../redux/ui-slice";
import { useAppDispatch, useAppSelector } from "../../../../assets/hooks/hooks";
import { Page } from "../../../../assets/components/PaginationBar/PaginationBar";
import Loader from "../../../../assets/components/Loader/Loader";
import { NotActivatedLicenseKey } from "./model";
import LicenseKeysNotActivatedTableHeader from "./LicenseKeysNotActivatedTable/LicenseKeysNotActivatedTableHeader";
import LicenseKeysNotActivatedTable from "./LicenseKeysNotActivatedTable/LicenseKeysNotActivatedTable";

const LicenseKeysNotActivated: FC = () => {
    const dispatch = useAppDispatch();
    const [products, setDataProducts] = useState<Products>([]);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});
    const loading: Loading = useAppSelector((state) => state.ui.loading);
    const pageSize: number = 30;
    const [dataTable, setData] = useState<NotActivatedLicenseKey[]>([]);
    const [pageNumber, setCurrentPage] = useState<Page>(1);
    const [totalLicenses, setTotalLicenses] = useState<number>(0);

    const {
        data: dataProducts,
        isSuccess: isSuccessProducts,
        isError: isErrorProducts,
        isLoading: isLoadingProducts,
    } = useGetProductsListQuery(
        {},
        {
            refetchOnFocus: true,
        }
    );

    const {
        data: dataNotActivatedLicenses,
        isLoading: isLoadingNotActivatedLicenses,
        isSuccess: isSuccessNotActivatedLicenses,
        isError: isErrorNotActivatedLicenses,
    } = useGetReportNotActivatedLicensesListQuery(
        {
            pageNumber,
            pageSize,
            orderId: quiresData.searchByOrderId,
            licenseKey: quiresData.searchInput,
            productId: quiresData.productId,
            startDate: quiresData.startDate,
            endDate: quiresData.endDate,
        },
        { refetchOnFocus: true }
    );

    const [
        downloadCSV,
        {
            isLoading: isLoadingDownloadCSV,
            isError: isErrorDownloadCSV,
            isSuccess: isSuccessDownloadCSV,
            reset: resetDownloadCSV,
        },
    ] = useDownloadCSVNotActivatedLicensesMutation();

    const triggerDownloadCSV = async () => {
        await downloadCSV({
            licenseKey: quiresData.searchInput,
            productId: quiresData.productId,
            startDate: quiresData.startDate,
            endDate: quiresData.endDate,
            pageNumber: 1,
            pageSize: totalLicenses,
        });
    };

    const handleDownloadCSVNotActivatedLicenses = useCallback(() => {
        if (isSuccessDownloadCSV) {
            toast.success("The products were successfully uploaded!");
            resetDownloadCSV();
        }

        if (isErrorDownloadCSV) {
            toast.error("The products were not uploaded!");
            resetDownloadCSV();
        }
    }, [isErrorDownloadCSV, isSuccessDownloadCSV, resetDownloadCSV]);

    const handleLicensesData = useCallback(() => {
        if (isSuccessNotActivatedLicenses && dataNotActivatedLicenses) {
            setData(dataNotActivatedLicenses.items);
            if (dataNotActivatedLicenses.filteredCount !== undefined) {
                setTotalLicenses(dataNotActivatedLicenses.filteredCount);
            } else {
                setTotalLicenses(dataNotActivatedLicenses.totalCount);
            }
        }
        if (isErrorNotActivatedLicenses) {
            toast.error("Error loading license keys");
        }
    }, [
        dataNotActivatedLicenses,
        isErrorNotActivatedLicenses,
        isSuccessNotActivatedLicenses,
    ]);

    const handleProductsData = useCallback(() => {
        if (isSuccessProducts && products) {
            setDataProducts(dataProducts.products);
        }
        if (isErrorProducts) {
            toast.error("Error loading products");
        }
    }, [isSuccessProducts, products, isErrorProducts, dataProducts]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal =
            isLoadingProducts ||
            isLoadingNotActivatedLicenses ||
            isLoadingDownloadCSV;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [
        isLoadingProducts,
        isLoadingNotActivatedLicenses,
        dispatch,
        isLoadingDownloadCSV,
    ]);

    useLayoutEffect(() => {
        handleLoadingState();
        handleLicensesData();
        handleProductsData();
        handleDownloadCSVNotActivatedLicenses();
    }, [
        handleLicensesData,
        handleLoadingState,
        handleProductsData,
        handleDownloadCSVNotActivatedLicenses,
    ]);

    return (
        <div className="page_with_table">
            <Loader loading={loading} />

            <PageTitle text="Not Activated License Keys" />
            <LicenseKeysNotActivatedSearchBar
                dataHandler={setQuires}
                products={products}
                dateRange={{
                    startDate: quiresData.startDate,
                    endDate: quiresData.endDate,
                }}
            />

            <div className="table_scroll_wrapper">
                <LicenseKeysNotActivatedTableHeader
                    triggerDownloadCSV={triggerDownloadCSV}
                    pageNumber={pageNumber}
                    sizePage={pageSize}
                    dataTable={dataTable}
                    totalLicenses={totalLicenses}
                    setCurrentPage={setCurrentPage}
                    currentSizePage={pageSize}
                />

                <LicenseKeysNotActivatedTable
                    licenseKeys={dataTable}
                    products={products}
                />
            </div>
        </div>
    );
};

export default LicenseKeysNotActivated;
